import { computed, ComputedRef, onBeforeMount, ref } from '@nuxtjs/composition-api';
import useLocalStorage from '@shared/composables/useLocalStorage';
import useVehicleManualSearch from '@shared/composables/useVehicleManualSearch';
import { UserVehicleSelectionKey } from '@unified-commerce/gpc-vue-storefront-search';

import {
  add,
  getFormattedTitleFull,
  getFormattedTitleShort,
  getFormattedTitleTiny,
  remove,
  select,
  toVehicleManualSearchSelection,
} from './helpers';
import { GarageVehicle } from './types';

const vehicles = ref<GarageVehicle[]>([]);
const selectedVehicle: ComputedRef<GarageVehicle | undefined> = computed(() =>
  vehicles.value.find((vehicle) => vehicle.selected),
);

const useMyGarage = () => {
  const { save, load } = useLocalStorage();
  const { search, results } = useVehicleManualSearch();

  const removeVehicle = (vehicle: GarageVehicle) => {
    const updatedVehicles = remove(vehicle, vehicles.value);
    vehicles.value = save(
      UserVehicleSelectionKey,
      vehicle.selected && updatedVehicles.length
        ? select(updatedVehicles[0], updatedVehicles)
        : updatedVehicles,
    );
  };

  const addVehicle = (vehicle: GarageVehicle) => {
    vehicles.value = save(UserVehicleSelectionKey, select(vehicle, add(vehicle, vehicles.value)));
  };

  const setSelectedVehicle = (vehicle: GarageVehicle) => {
    vehicles.value = save(UserVehicleSelectionKey, select(vehicle, vehicles.value));
  };

  const updateVehicle = async (vehicle: GarageVehicle) => {
    await search(toVehicleManualSearchSelection(vehicle));
    if (results.value?.[0]) {
      removeVehicle(vehicle);
      addVehicle(results.value?.[0]);
    }
  };

  onBeforeMount(() => {
    vehicles.value = load(UserVehicleSelectionKey) ?? [];
  });

  return {
    vehicles,
    selectedVehicle,
    addVehicle,
    removeVehicle,
    updateVehicle,
    setSelectedVehicle,
    getFormattedTitleFull,
    getFormattedTitleShort,
    getFormattedTitleTiny,
  };
};

export default useMyGarage;
